/**
 * Estimate Card Component
 * BuilderBel Construction Project Management System
 *
 * Card display for estimates in list view
 */

import React from 'react';
import { MoreHorizontal, Copy, Send, CheckCircle, XCircle, FileText, Edit2 } from 'lucide-react';
import type { Estimate, EstimateType } from '../../types/estimate';
import StatusBadge from './StatusBadge';

export interface EstimateCardProps {
  estimate: Estimate;
  onSelect: (estimate: Estimate) => void;
  onEdit?: (estimate: Estimate) => void;
  onSubmitForReview?: (estimate: Estimate) => void;
  onApprove?: (estimate: Estimate) => void;
  onReject?: (estimate: Estimate) => void;
  onCreateVersion?: (estimate: Estimate) => void;
  onDelete?: (estimate: Estimate) => void;
}

const typeLabels: Record<EstimateType, string> = {
  conceptual: 'Conceptual',
  preliminary: 'Preliminary',
  budget: 'Budget',
  detailed: 'Detailed',
  change_order: 'Change Order',
  final: 'Final',
};

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const EstimateCard: React.FC<EstimateCardProps> = ({
  estimate,
  onSelect,
  onEdit,
  onSubmitForReview,
  onApprove,
  onReject,
  onCreateVersion,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);

  const canEdit = estimate.status === 'draft';
  const canSubmitForReview = estimate.status === 'draft';
  const canApprove = estimate.status === 'pending_review';
  const canReject = estimate.status === 'pending_review';
  const canCreateVersion = estimate.status === 'approved';
  const canDelete = estimate.status === 'draft';

  const handleAction = (action: () => void) => {
    setShowMenu(false);
    action();
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 hover:border-brand-300 hover:shadow-md transition-all cursor-pointer group"
      onClick={() => onSelect(estimate)}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-500">{estimate.estimateNumber}</span>
              {estimate.version > 1 && (
                <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                  v{estimate.version}
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-gray-900 truncate group-hover:text-brand-600">
              {estimate.name}
            </h3>
          </div>

          <div className="flex items-center gap-2 ml-3">
            <StatusBadge status={estimate.status} size="sm" />
            <div className="relative">
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={e => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    {canEdit && onEdit && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleAction(() => onEdit(estimate));
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                    {canSubmitForReview && onSubmitForReview && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleAction(() => onSubmitForReview(estimate));
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Send className="h-4 w-4" />
                        Submit for Review
                      </button>
                    )}
                    {canApprove && onApprove && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleAction(() => onApprove(estimate));
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approve
                      </button>
                    )}
                    {canReject && onReject && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleAction(() => onReject(estimate));
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </button>
                    )}
                    {canCreateVersion && onCreateVersion && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleAction(() => onCreateVersion(estimate));
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Create New Version
                      </button>
                    )}
                    {canDelete && onDelete && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleAction(() => onDelete(estimate));
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <XCircle className="h-4 w-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {estimate.description && (
          <p className="text-sm text-gray-500 mb-4 line-clamp-2">{estimate.description}</p>
        )}

        {/* Type Badge */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-md flex items-center gap-1">
            <FileText className="h-3 w-3" />
            {typeLabels[estimate.estimateType]}
          </span>
          {estimate.lineItemCount !== undefined && (
            <span className="text-xs text-gray-500">{estimate.lineItemCount} line items</span>
          )}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500 block">Subtotal</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(estimate.subtotalDirect)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Markups</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(estimate.totalMarkup)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Grand Total</span>
            <span className="text-sm font-semibold text-green-600">
              {formatCurrency(estimate.grandTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-gray-50 rounded-b-xl flex items-center justify-between text-xs text-gray-500">
        <span>Created {formatDate(estimate.createdAt)}</span>
        {estimate.updatedAt !== estimate.createdAt && (
          <span>Updated {formatDate(estimate.updatedAt)}</span>
        )}
      </div>
    </div>
  );
};

export default EstimateCard;
